import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { COMPANY_META, type CompanyId } from '@igf-minar/shared'
import { api, type AdminUser, type UserRole } from '../lib/api'
import { useCurrentUser, isAdminRole, isSuperAdminRole } from '../lib/auth'
import { validatePassword, PASSWORD_RULE_TEXT, PASSWORD_MISMATCH } from '../lib/password'
import { PasswordChecklist } from '../components/PasswordChecklist'

export const Route = createFileRoute('/admin/users')({
  component: AdminUsersPage,
})

const ROLE_LABEL: Record<UserRole, string> = {
  superadmin: 'Superadmin (Laufið)',
  admin: 'Admin (ÍGF)',
  user: 'Viðskiptavinur',
}

const ROLE_BADGE: Record<UserRole, string> = {
  superadmin: 'bg-purple-50 text-purple-700 ring-purple-200',
  admin: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  user: 'bg-gray-100 text-gray-600 ring-gray-200',
}

type SortKey = 'email' | 'displayName' | 'role' | 'status'

const ROLE_ORDER: Record<UserRole, number> = { superadmin: 0, admin: 1, user: 2 }

const companyName = (id: string): string =>
  (COMPANY_META as Record<string, { name: string }>)[id]?.name ?? id

/** Comparable string for each sortable column (padded so numeric-ish keys order right). */
function sortValue(u: AdminUser, key: SortKey): string {
  switch (key) {
    case 'email':
      return u.email.toLowerCase()
    case 'displayName':
      return (u.displayName ?? '').toLowerCase()
    case 'role':
      return String(ROLE_ORDER[u.role])
    case 'status':
      return u.isActive ? '0' : '1'
  }
}

function AdminUsersPage() {
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const qc = useQueryClient()
  const isSuper = isSuperAdminRole(me)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.admin.listUsers(),
    enabled: isAdminRole(me),
  })

  // Company names for the access column (incl. Habilis-provisioned companies not
  // in the static list). Admins have '*' access so this returns every company.
  const { data: companyData } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.companies(),
    enabled: isAdminRole(me),
  })
  const nameOf = useMemo(() => {
    const map = new Map((companyData?.companies ?? []).map((c) => [c.id, c.name]))
    return (id: string) => map.get(id) ?? companyName(id)
  }, [companyData])

  const [createOpen, setCreateOpen] = useState(false)
  const [editUser, setEditUser] = useState<AdminUser | null>(null)
  const [accessUser, setAccessUser] = useState<AdminUser | null>(null)

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'email', dir: 'asc' })

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))

  const rows = useMemo(() => {
    const all = data?.users ?? []
    const q = search.trim().toLowerCase()
    const filtered = all.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false
      if (statusFilter === 'active' && !u.isActive) return false
      if (statusFilter === 'inactive' && u.isActive) return false
      if (q && !`${u.email} ${u.displayName ?? ''}`.toLowerCase().includes(q)) return false
      return true
    })
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => sortValue(a, sort.key).localeCompare(sortValue(b, sort.key), 'is') * dir)
  }, [data, search, roleFilter, statusFilter, sort])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] })

  if (meLoading) return <PageShell><p className="text-[var(--muted)]">Hleð…</p></PageShell>

  if (!isAdminRole(me)) {
    return (
      <PageShell>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
          <h2 className="font-semibold">Enginn aðgangur</h2>
          <p className="mt-1 text-sm">Þú þarft admin-réttindi til að sjá notendastjórnun.</p>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--fg)]">Notendur</h1>
          <p className="text-sm text-[var(--muted)] mt-1">
            {isSuper
              ? 'Superadmin: þú getur stýrt öllum notendum, þ.m.t. admin-um ÍGF.'
              : 'Admin: þú getur stýrt viðskiptavina-notendum og aðgangi þeirra.'}
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="bg-[var(--brand)] hover:bg-[var(--brand-strong)] text-[var(--on-brand)] text-sm font-medium rounded-lg px-4 py-2"
        >
          + Nýr notandi
        </button>
      </div>

      {isLoading && <p className="text-[var(--muted)]">Sæki notendur…</p>}
      {error && <p className="text-red-500">{(error as Error).message}</p>}

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Leita eftir nafni eða netfangi…"
              className="flex-1 min-w-[220px] rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--fg)] outline-none focus:border-[var(--brand)] placeholder:text-[var(--faint)]"
            />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as UserRole | 'all')}
              className="rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--fg)] outline-none focus:border-[var(--brand)]"
            >
              <option value="all">Öll hlutverk</option>
              <option value="superadmin">{ROLE_LABEL.superadmin}</option>
              <option value="admin">{ROLE_LABEL.admin}</option>
              <option value="user">{ROLE_LABEL.user}</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--fg)] outline-none focus:border-[var(--brand)]"
            >
              <option value="all">Öll staða</option>
              <option value="active">Virkir</option>
              <option value="inactive">Óvirkir</option>
            </select>
          </div>

          <div className="bg-[var(--surface)] shadow-[var(--elev-sm)] rounded-xl overflow-hidden border border-[var(--border)]">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[var(--surface-2)]">
                <tr className="text-left text-xs uppercase tracking-wide text-[var(--muted)]">
                  <SortHeader label="Netfang" col="email" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Nafn" col="displayName" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Hlutverk" col="role" sort={sort} onSort={toggleSort} />
                  <th className="px-4 py-3 font-medium">Aðgangur</th>
                  <SortHeader label="Staða" col="status" sort={sort} onSort={toggleSort} />
                  <th className="px-4 py-3 font-medium text-right">Aðgerðir</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[var(--faint)]">
                      Enginn notandi passar við leitina.
                    </td>
                  </tr>
                )}
                {rows.map((u) => {
                  const canManage = isSuper || u.role === 'user'
                  return (
                    <tr key={u.id} className="hover:bg-[var(--surface-2)]">
                      <td className="px-4 py-3 text-[var(--fg)]">{u.email}</td>
                      <td className="px-4 py-3 text-[var(--muted)]">{u.displayName || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${ROLE_BADGE[u.role]}`}>
                          {ROLE_LABEL[u.role]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[var(--muted)]">
                        {u.companyIds === '*'
                          ? <span className="text-[var(--faint)]">Öll fyrirtæki</span>
                          : `${u.companyIds.length} fyrirtæki`}
                        {u.linkedCompanyIds.length > 0 && (
                          <div className="mt-0.5 text-[11px] text-[var(--faint)]" title={u.linkedCompanyIds.map(nameOf).join(', ')}>
                            Habilis: {u.linkedCompanyIds.map(nameOf).join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.isActive
                          ? <span className="text-emerald-500 text-xs font-medium">Virkur</span>
                          : <span className="text-[var(--faint)] text-xs">Óvirkur</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {u.role === 'user' && (
                            <button
                              onClick={() => setAccessUser(u)}
                              className="text-xs text-[var(--brand)] hover:underline"
                            >
                              Aðgangur
                            </button>
                          )}
                          <button
                            onClick={() => setEditUser(u)}
                            disabled={!canManage}
                            className="text-xs text-[var(--muted)] hover:underline disabled:opacity-40 disabled:no-underline"
                          >
                            Breyta
                          </button>
                          <DeleteButton user={u} me={me?.id} canManage={canManage && u.id !== me?.id} onDone={invalidate} />
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}

      {createOpen && (
        <CreateUserModal isSuper={isSuper} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); invalidate() }} />
      )}
      {editUser && (
        <EditUserModal user={editUser} isSuper={isSuper} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); invalidate() }} />
      )}
      {accessUser && (
        <AccessModal user={accessUser} onClose={() => setAccessUser(null)} onDone={() => { setAccessUser(null); invalidate() }} />
      )}
    </PageShell>
  )
}

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
}

function SortHeader({
  label,
  col,
  sort,
  onSort,
}: {
  label: string
  col: SortKey
  sort: { key: SortKey; dir: 'asc' | 'desc' }
  onSort: (key: SortKey) => void
}) {
  const active = sort.key === col
  return (
    <th className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => onSort(col)}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-[var(--fg)]"
      >
        {label}
        <span className={active ? 'text-[var(--brand)]' : 'text-[var(--faint)]'}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  )
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] rounded-2xl shadow-[var(--shadow-lift)] w-full max-w-md max-h-[90vh] overflow-y-auto border border-[var(--border)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h2 className="font-semibold text-[var(--fg)]">{title}</h2>
          <button onClick={onClose} className="text-[var(--faint)] hover:text-[var(--fg)] text-xl leading-none">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

function inputCls() {
  return 'mt-1 w-full rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--fg)] outline-none focus:border-[var(--brand)] placeholder:text-[var(--faint)]'
}

function CreateUserModal({ isSuper, onClose, onDone }: { isSuper: boolean; onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [role, setRole] = useState<UserRole>('user')
  const [companyIds, setCompanyIds] = useState<CompanyId[]>([])
  const [error, setError] = useState<string | null>(null)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const pwError = validatePassword(password)
    if (pwError) { setError(pwError); return }
    if (password !== confirmPassword) { setError(PASSWORD_MISMATCH); return }
    mutation.mutate()
  }

  const mutation = useMutation({
    mutationFn: () =>
      api.admin.createUser({
        email,
        displayName: displayName || undefined,
        password,
        role,
        companyIds: role === 'user' ? companyIds : undefined,
      }),
    onSuccess: onDone,
    onError: (e) => setError((e as Error).message),
  })

  return (
    <Modal title="Nýr notandi" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--fg-soft)]">Nafn</label>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputCls()} />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--fg-soft)]">Netfang</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls()} />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--fg-soft)]">Lykilorð</label>
          <input type="text" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls()} placeholder={PASSWORD_RULE_TEXT} />
          <PasswordChecklist password={password} confirm={confirmPassword} />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--fg-soft)]">Staðfestu lykilorð</label>
          <input type="text" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputCls()} placeholder="Sláðu inn sama lykilorð aftur" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--fg-soft)]">Hlutverk</label>
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} className={inputCls()}>
            <option value="user">{ROLE_LABEL.user}</option>
            {isSuper && <option value="admin">{ROLE_LABEL.admin}</option>}
            {isSuper && <option value="superadmin">{ROLE_LABEL.superadmin}</option>}
          </select>
        </div>
        {role === 'user' && (
          <div>
            <label className="block text-sm font-medium text-[var(--fg-soft)] mb-1">Fyrirtækjaaðgangur</label>
            <CompanyChecklist selected={companyIds} onChange={setCompanyIds} />
          </div>
        )}
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[var(--muted)] hover:bg-[var(--surface-2)] rounded-lg">Hætta við</button>
          <button type="submit" disabled={mutation.isPending} className="px-4 py-2 text-sm text-[var(--on-brand)] bg-[var(--brand)] hover:bg-[var(--brand-strong)] disabled:opacity-50 rounded-lg">
            {mutation.isPending ? 'Vista…' : 'Stofna'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function EditUserModal({ user, isSuper, onClose, onDone }: { user: AdminUser; isSuper: boolean; onClose: () => void; onDone: () => void }) {
  const [displayName, setDisplayName] = useState(user.displayName ?? '')
  const [role, setRole] = useState<UserRole>(user.role)
  const [isActive, setIsActive] = useState(user.isActive)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [cred, setCred] = useState<{ email: string; tempPassword: string; loginUrl: string; emailSent: boolean } | null>(null)
  const [copied, setCopied] = useState(false)

  const sendCredMutation = useMutation({
    mutationFn: () => api.admin.sendCredentials(user.id),
    onSuccess: (res) => { setCred(res); setError(null) },
    onError: (e) => setError((e as Error).message),
  })

  const loginUrl = `${window.location.origin}/login`

  async function copyCredentials() {
    if (!cred) return
    const text = `Slóð: ${loginUrl}\nNotandanafn: ${cred.email}\nLykilorð: ${cred.tempPassword}`
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard blocked — user can select manually */
    }
  }

  const mutation = useMutation({
    mutationFn: () =>
      api.admin.updateUser(user.id, {
        displayName: displayName !== (user.displayName ?? '') ? displayName : undefined,
        role: isSuper && role !== user.role ? role : undefined,
        isActive: isActive !== user.isActive ? isActive : undefined,
        password: password || undefined,
      }),
    onSuccess: onDone,
    onError: (e) => setError((e as Error).message),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password) {
      const pwError = validatePassword(password)
      if (pwError) { setError(pwError); return }
      if (password !== confirmPassword) { setError(PASSWORD_MISMATCH); return }
    }
    mutation.mutate()
  }

  return (
    <Modal title={`Breyta: ${user.email}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--fg-soft)]">Nafn</label>
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputCls()} />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--fg-soft)]">Hlutverk</label>
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)} disabled={!isSuper} className={inputCls()}>
            <option value="user">{ROLE_LABEL.user}</option>
            <option value="admin">{ROLE_LABEL.admin}</option>
            <option value="superadmin">{ROLE_LABEL.superadmin}</option>
          </select>
          {!isSuper && <p className="text-xs text-[var(--faint)] mt-1">Aðeins superadmin má breyta hlutverki.</p>}
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--fg-soft)]">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded border-[var(--border)] accent-[var(--brand)]" />
          Virkur reikningur
        </label>
        <div>
          <label className="block text-sm font-medium text-[var(--fg-soft)]">Nýtt lykilorð</label>
          <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls()} placeholder="Skildu eftir tómt til að halda óbreyttu" />
          {password
            ? <PasswordChecklist password={password} confirm={confirmPassword} />
            : <p className="mt-1 text-xs text-[var(--faint)]">{PASSWORD_RULE_TEXT}</p>}
        </div>
        {password && (
          <div>
            <label className="block text-sm font-medium text-[var(--fg-soft)]">Staðfestu nýtt lykilorð</label>
            <input type="text" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputCls()} placeholder="Sláðu inn sama lykilorð aftur" />
          </div>
        )}

        <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium text-[var(--fg-soft)]">Senda aðgangsupplýsingar</p>
              <p className="text-xs text-[var(--muted)] mt-0.5">
                Býr til nýtt bráðabirgðalykilorð, sendir notandanum slóð + notandanafn + lykilorð í tölvupósti og þvingar breytingu við fyrstu innskráningu.
              </p>
            </div>
            <button
              type="button"
              onClick={() => sendCredMutation.mutate()}
              disabled={sendCredMutation.isPending}
              className="shrink-0 px-3 py-2 text-sm text-[var(--brand)] border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-2)] disabled:opacity-50 rounded-lg font-medium"
            >
              {sendCredMutation.isPending ? 'Sendi…' : 'Senda aðgang'}
            </button>
          </div>

          {cred && (
            <div className="mt-3 rounded-lg border border-[var(--border)] bg-[var(--surface)] p-3 text-sm">
              <p className={cred.emailSent ? 'text-emerald-500' : 'text-amber-500'}>
                {cred.emailSent
                  ? `Tölvupóstur sendur á ${cred.email}.`
                  : `Tókst ekki að senda tölvupóst — afritaðu upplýsingarnar og sendu handvirkt.`}
              </p>
              <dl className="mt-2 space-y-1 text-[var(--fg-soft)]">
                <div className="flex gap-2"><dt className="w-24 text-[var(--faint)]">Slóð</dt><dd className="font-mono break-all">{loginUrl}</dd></div>
                <div className="flex gap-2"><dt className="w-24 text-[var(--faint)]">Notandanafn</dt><dd className="font-mono break-all">{cred.email}</dd></div>
                <div className="flex gap-2"><dt className="w-24 text-[var(--faint)]">Lykilorð</dt><dd className="font-mono break-all">{cred.tempPassword}</dd></div>
              </dl>
              <div className="mt-2 flex items-center gap-2">
                <button type="button" onClick={copyCredentials} className="text-xs text-[var(--brand)] hover:underline font-medium">
                  {copied ? 'Afritað!' : 'Afrita upplýsingar'}
                </button>
                <a
                  href={`mailto:${cred.email}?subject=${encodeURIComponent('Aðgangur að Mínum síðum ÍGF')}&body=${encodeURIComponent(`Slóð: ${loginUrl}\nNotandanafn: ${cred.email}\nLykilorð: ${cred.tempPassword}\n\nÞú þarft að velja nýtt lykilorð við fyrstu innskráningu.`)}`}
                  className="text-xs text-[var(--muted)] hover:underline"
                >
                  Opna í tölvupóstforriti
                </a>
              </div>
            </div>
          )}
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[var(--muted)] hover:bg-[var(--surface-2)] rounded-lg">Hætta við</button>
          <button type="submit" disabled={mutation.isPending} className="px-4 py-2 text-sm text-[var(--on-brand)] bg-[var(--brand)] hover:bg-[var(--brand-strong)] disabled:opacity-50 rounded-lg">
            {mutation.isPending ? 'Vista…' : 'Vista'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function AccessModal({ user, onClose, onDone }: { user: AdminUser; onClose: () => void; onDone: () => void }) {
  const initial = user.companyIds === '*' ? [] : (user.companyIds as CompanyId[])
  const [companyIds, setCompanyIds] = useState<CompanyId[]>(initial)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => api.admin.setAccess(user.id, companyIds),
    onSuccess: onDone,
    onError: (e) => setError((e as Error).message),
  })

  return (
    <Modal title={`Aðgangur: ${user.email}`} onClose={onClose}>
      <p className="text-sm text-[var(--muted)] mb-3">Veldu hvaða fyrirtæki þessi viðskiptavinur má sjá.</p>
      <CompanyChecklist selected={companyIds} onChange={setCompanyIds} />
      {error && <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}
      <div className="flex justify-end gap-2 pt-4">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[var(--muted)] hover:bg-[var(--surface-2)] rounded-lg">Hætta við</button>
        <button onClick={() => { setError(null); mutation.mutate() }} disabled={mutation.isPending} className="px-4 py-2 text-sm text-[var(--on-brand)] bg-[var(--brand)] hover:bg-[var(--brand-strong)] disabled:opacity-50 rounded-lg">
          {mutation.isPending ? 'Vista…' : 'Vista aðgang'}
        </button>
      </div>
    </Modal>
  )
}

function CompanyChecklist({ selected, onChange }: { selected: CompanyId[]; onChange: (ids: CompanyId[]) => void }) {
  const sel = useMemo(() => new Set(selected), [selected])
  // Source the list from the API so Habilis-provisioned companies are grantable
  // too (admins have '*' access, so this returns every company).
  const { data, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.companies(),
  })
  const companies = data?.companies ?? []

  function toggle(id: CompanyId) {
    const next = new Set(sel)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next] as CompanyId[])
  }
  return (
    <div className="max-h-56 overflow-y-auto border border-[var(--border)] rounded-lg divide-y divide-[var(--border)]">
      {isLoading && <p className="px-3 py-2 text-sm text-[var(--faint)]">Sæki fyrirtæki…</p>}
      {!isLoading && companies.length === 0 && (
        <p className="px-3 py-2 text-sm text-[var(--faint)]">Engin fyrirtæki fundust.</p>
      )}
      {companies.map((c) => (
        <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--fg-soft)] hover:bg-[var(--surface-2)] cursor-pointer">
          <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} className="rounded border-[var(--border)] accent-[var(--brand)]" />
          {c.name}
        </label>
      ))}
    </div>
  )
}

function DeleteButton({ user, me, canManage, onDone }: { user: AdminUser; me?: string; canManage: boolean; onDone: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const mutation = useMutation({
    mutationFn: () => api.admin.deleteUser(user.id),
    onSuccess: onDone,
  })
  if (user.id === me) return null
  if (confirming) {
    return (
      <span className="inline-flex items-center gap-1">
        <button onClick={() => mutation.mutate()} disabled={mutation.isPending} className="text-xs text-red-500 font-medium hover:underline">
          Staðfesta
        </button>
        <button onClick={() => setConfirming(false)} className="text-xs text-[var(--faint)] hover:underline">Nei</button>
      </span>
    )
  }
  return (
    <button
      onClick={() => setConfirming(true)}
      disabled={!canManage}
      className="text-xs text-red-500 hover:underline disabled:opacity-40 disabled:no-underline"
    >
      Eyða
    </button>
  )
}
